/**
 * Markdown-Scanner für die Quellansicht (#2242).
 *
 * Reines Erkennen von Markdown auf Textzeilen, ohne Editor-Abhängigkeit.
 * Zwei Editoren nutzen ihn und dieselben CSS-Klassen, damit Markdown im
 * Portal überall gleich aussieht:
 *
 *   - MarkdownSource.ts        (TipTap, Collab-Beiträge, Absätze)
 *   - MarkdownCodeEditor.tsx   (CodeMirror, statische Beiträge, Zeilen)
 *
 * Der Scanner formatiert nichts um. Er liefert nur Bereiche mit Klassen.
 */

export interface MdSpan {
  from: number
  to: number
  cls: string
}

export interface MdLineResult {
  /** Klasse für die ganze Zeile, etwa `md-h2`, `md-li` oder `md-code-block`. */
  block: string | null
  /** Bereiche relativ zum Zeilenanfang. */
  spans: MdSpan[]
  /** Zeile liegt in einem Fence (ohne die Fence-Zeilen selbst). */
  code: boolean
}

export interface MdScanResult {
  /** Klasse für den ganzen Absatz. */
  block: string | null
  spans: MdSpan[]
}

export interface MdScanState {
  inFence: boolean
  fenceChar: string
  fenceLen: number
}

export const initialScanState = (): MdScanState => ({ inFence: false, fenceChar: '', fenceLen: 0 })

const RE_FENCE = /^\s{0,3}(`{3,}|~{3,})/
const RE_HEADING = /^(#{1,6})(\s+)/
const RE_QUOTE = /^(>\s?)+/
export const RE_LIST = /^(\s*)([-*+]|\d{1,9}[.)])(\s+)(\[[ xX]\]\s+)?/
const RE_HR = /^\s{0,3}([-*_])(\s*\1){2,}\s*$/
const RE_IA_BREAK = /^\+\+\+\s*$/
const RE_IA_TOC = /^\{\{TOC\}\}\s*$/
const RE_TABLE_ROW = /^\s*\|.*\|\s*$/
/** Zeilenpräfixe, die die Werkzeugleiste setzt oder ersetzt. */
export const RE_BLOCK_PREFIX = /^(#{1,6}\s+|>\s?|(?:[-*+]|\d{1,9}[.)])\s+(?:\[[ xX]\]\s+)?)/

const RE_CODE = /(`+)([^`]|[^`][\s\S]*?[^`])\1(?!`)/g
const RE_IMAGE = /!\[([^\]]*)\]\(([^)\s]*)(\s+"[^"]*")?\)/g
const RE_LINK = /\[([^\]]+)\]\(([^)\s]*)(\s+"[^"]*")?\)/g
const RE_URL = /(?<![(\]])\bhttps?:\/\/[^\s<>)]+/g
const RE_STRONG = /(\*\*|__)(?=\S)([\s\S]*?\S)\1/g
const RE_EM = /(?<![*_\w])(\*|_)(?=\S)([^*_\n]*?\S)\1(?![*_\w])/g
const RE_STRIKE = /~~(?=\S)([\s\S]*?\S)~~/g

/** Scannt eine Zeile ohne Block-Präfix nach Inline-Markdown. */
function scanInline(line: string, base: number, spans: MdSpan[]): void {
  const taken = new Uint8Array(line.length)
  const free = (a: number, b: number) => {
    for (let i = a; i < b; i++) if (taken[i]) return false
    return true
  }
  const take = (a: number, b: number) => {
    for (let i = a; i < b; i++) taken[i] = 1
  }
  const push = (a: number, b: number, cls: string) => {
    if (b > a) spans.push({ from: base + a, to: base + b, cls })
  }

  let m: RegExpExecArray | null
  RE_CODE.lastIndex = 0
  while ((m = RE_CODE.exec(line))) {
    const a = m.index
    const b = a + m[0].length
    const tick = m[1].length
    push(a, a + tick, 'md-marker')
    push(a + tick, b - tick, 'md-code')
    push(b - tick, b, 'md-marker')
    take(a, b)
  }
  for (const [re, textCls] of [[RE_IMAGE, 'md-image'], [RE_LINK, 'md-link-text']] as const) {
    re.lastIndex = 0
    while ((m = re.exec(line))) {
      const a = m.index
      const b = a + m[0].length
      if (!free(a, b)) continue
      const open = re === RE_IMAGE ? 2 : 1 // "![" oder "["
      const textEnd = a + open + m[1].length
      push(a, a + open, 'md-marker')
      push(a + open, textEnd, textCls)
      push(textEnd, textEnd + 2, 'md-marker') // "]("
      push(textEnd + 2, b - 1, 'md-link-url')
      push(b - 1, b, 'md-marker')
      take(a, b)
    }
  }
  RE_URL.lastIndex = 0
  while ((m = RE_URL.exec(line))) {
    const a = m.index
    const b = a + m[0].length
    if (!free(a, b)) continue
    push(a, b, 'md-link-url')
    take(a, b)
  }
  for (const [re, cls] of [[RE_STRONG, 'md-strong'], [RE_STRIKE, 'md-strike'], [RE_EM, 'md-em']] as const) {
    re.lastIndex = 0
    while ((m = re.exec(line))) {
      const a = m.index
      const b = a + m[0].length
      if (!free(a, b)) continue
      const mark = re === RE_STRIKE ? 2 : m[1].length
      push(a, a + mark, 'md-marker')
      push(a + mark, b - mark, cls)
      push(b - mark, b, 'md-marker')
    }
  }
}

/**
 * Führt nur den Fence-Zustand über eine Zeile fort, ohne Bereiche zu
 * erzeugen. Für Zeilen außerhalb des sichtbaren Ausschnitts.
 */
export function advanceFence(line: string, state: MdScanState): void {
  const fence = RE_FENCE.exec(line)
  if (!fence) return
  if (state.inFence) {
    if (fence[1][0] === state.fenceChar && fence[1].length >= state.fenceLen) state.inFence = false
  } else {
    state.inFence = true
    state.fenceChar = fence[1][0]
    state.fenceLen = fence[1].length
  }
}

/** Scannt eine einzelne Zeile. `state` trägt den Fence-Zustand weiter. */
export function scanMarkdownLine(line: string, state: MdScanState): MdLineResult {
  const spans: MdSpan[] = []
  const fence = RE_FENCE.exec(line)
  if (state.inFence) {
    if (fence && fence[1][0] === state.fenceChar && fence[1].length >= state.fenceLen) {
      state.inFence = false
      spans.push({ from: 0, to: line.length, cls: 'md-marker md-fence-line' })
      return { block: 'md-fence', spans, code: false }
    }
    spans.push({ from: 0, to: line.length, cls: 'md-code-line' })
    return { block: 'md-code-block', spans, code: true }
  }
  if (fence) {
    state.inFence = true
    state.fenceChar = fence[1][0]
    state.fenceLen = fence[1].length
    spans.push({ from: 0, to: line.length, cls: 'md-marker md-fence-line' })
    return { block: 'md-fence', spans, code: false }
  }

  let m: RegExpExecArray | null
  let prefix = 0
  let block: string | null = null
  if (RE_IA_BREAK.test(line) || RE_IA_TOC.test(line)) {
    // iA-Writer-Konventionen: Seitenumbruch und Inhaltsverzeichnis.
    spans.push({ from: 0, to: line.length, cls: 'md-marker' })
    block = 'md-hr'
    prefix = line.length
  } else if (RE_HR.test(line)) {
    spans.push({ from: 0, to: line.length, cls: 'md-marker' })
    block = 'md-hr'
    prefix = line.length
  } else if ((m = RE_HEADING.exec(line))) {
    prefix = m[0].length
    block = `md-h${m[1].length}`
    spans.push({ from: 0, to: prefix, cls: 'md-marker' })
  } else if ((m = RE_QUOTE.exec(line))) {
    prefix = m[0].length
    block = 'md-quote'
    spans.push({ from: 0, to: prefix, cls: 'md-marker' })
  } else if ((m = RE_LIST.exec(line))) {
    prefix = m[0].length
    block = 'md-li'
    spans.push({ from: m[1].length, to: prefix, cls: 'md-marker md-bullet' })
  } else if (RE_TABLE_ROW.test(line)) {
    // Tabellenzeilen bleiben Quelltext, aber in fester Schriftbreite, damit
    // die Spalten untereinander stehen.
    block = 'md-table'
  }
  if (prefix < line.length) scanInline(line.slice(prefix), prefix, spans)
  return { block, spans, code: false }
}

/**
 * Scannt einen Absatz, der mehrere Zeilen enthalten kann. Die Absatzklasse
 * kommt aus der ersten Zeile; enthält der Absatz Code-Zeilen, ist er ein
 * Codeblock, auch wenn er mit der Fence-Zeile beginnt.
 */
export function scanMarkdownParagraph(text: string, state: MdScanState): MdScanResult {
  const spans: MdSpan[] = []
  let block: string | null = null
  let hasCode = false
  let offset = 0
  const lines = text.split('\n')
  for (let li = 0; li < lines.length; li++) {
    const res = scanMarkdownLine(lines[li], state)
    if (li === 0) block = res.block
    if (res.code) hasCode = true
    for (const s of res.spans) spans.push({ from: offset + s.from, to: offset + s.to, cls: s.cls })
    offset += lines[li].length + 1
  }
  if (hasCode) block = 'md-code-block'
  return { block, spans }
}

/** Nächster Listenmarker für die Fortführung per Enter, oder null. */
export function nextListMarker(line: string): { prefix: string; emptyItem: boolean; length: number } | null {
  const m = RE_LIST.exec(line)
  if (!m) return null
  const num = /^\d+/.exec(m[2])
  const bullet = num ? `${Number(num[0]) + 1}${m[2].slice(num[0].length)}` : m[2]
  const task = m[4] ? '[ ] ' : ''
  return { prefix: `${m[1]}${bullet} ${task}`, emptyItem: line.length === m[0].length, length: m[0].length }
}

/** Was die Werkzeugleiste von einem Markdown-Editor braucht. */
export interface MarkdownToolbarApi {
  wrap: (marker: string) => void
  prefix: (prefix: string) => void
  link: () => void
  fence: () => void
  table: () => void
}

export const MD_TABLE_SKELETON = '| Spalte | Spalte |\n|---|---|\n| Wert | Wert |'
